import { useState } from 'react';
import { Eye, EyeOff, Loader2, ShieldCheck, Lock, Info } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiClient } from '@/api/client';
import { usePortalAuthStore } from '@/store/portal-auth.store';
import type { PortalSessionToken } from '@/store/portal-auth.store';
import { useCaptchaConfig } from '@/api/hooks/useCaptchaConfig';
import { TurnstileWidget } from '@/components/TurnstileWidget';

type LoginStep = 'email' | 'credentials' | 'mfa' | 'reverification';

// ── Lookup options result ─────────────────────────────────────────────────────

interface LookupOptionsResult {
  has_password: boolean;
  federated_providers: Array<'google' | 'microsoft'>;
}

// ── OAuth login CTA ───────────────────────────────────────────────────────────

const PROVIDER_LABELS: Record<string, string> = { google: 'Google', microsoft: 'Microsoft' };

// Official third-party brand marks. Brand SVGs are the ONE legitimate exception to the
// "tokens only / no hardcoded hex" rule — Google/Microsoft logo colors are fixed brand
// assets and cannot be tokenized. Presentation-only (replaces the prior "G"/"M" glyphs).
function ProviderLogo({ provider }: { provider: string }) {
  if (provider === 'google') {
    return (
      <svg viewBox="0 0 48 48" className="h-[18px] w-[18px]" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 23 23" className="h-[17px] w-[17px]" aria-hidden="true">
      <path fill="#F25022" d="M0 0h11v11H0z" />
      <path fill="#7FBA00" d="M12 0h11v11H12z" />
      <path fill="#00A4EF" d="M0 12h11v11H0z" />
      <path fill="#FFB900" d="M12 12h11v11H12z" />
    </svg>
  );
}

function OAuthLoginButton({ provider }: { provider: string }) {
  const initMut = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post(`/portal/auth/oauth/${provider}/init`, { flow: 'login' });
      return data.data as { authorization_url: string };
    },
    onSuccess: ({ authorization_url }) => { window.location.href = authorization_url; },
    onError: () => { toast.error(`Could not start ${PROVIDER_LABELS[provider] ?? provider} sign-in.`); },
  });

  return (
    <Button
      type="button"
      variant="outline"
      className="h-11 w-full gap-3 rounded-lg border-white/10 bg-white/[0.03] font-semibold hover:border-white/20 hover:bg-white/[0.06]"
      disabled={initMut.isPending}
      onClick={() => initMut.mutate()}
    >
      {initMut.isPending
        ? <Loader2 className="h-4 w-4 animate-spin" />
        : <ProviderLogo provider={provider} />
      }
      Continue with {PROVIDER_LABELS[provider] ?? provider}
    </Button>
  );
}

// ── OAuth error messages ──────────────────────────────────────────────────────

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  no_account: 'No account found for this Google or Microsoft identity. Sign in with your email and password first.',
  account_disabled: 'This account has been disabled. Contact your administrator for assistance.',
  login_failed: 'Sign-in could not be completed. Please try again.',
  idp_denied: 'Sign-in was cancelled or denied.',
  token_validation_failed: 'Sign-in could not be completed. Please try again.',
};

export function PortalLoginPage() {
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaSessionToken, setMfaSessionToken] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [accountDisabled, setAccountDisabled] = useState(false);
  const [lookupResult, setLookupResult] = useState<LookupOptionsResult | null>(null);

  // ADMINSEC-3c-2b — uniform Turnstile CAPTCHA on the credentials step (render-when-enabled).
  const { enabled: captchaEnabled, siteKey: captchaSiteKey } = useCaptchaConfig();
  const showCaptcha = captchaEnabled && !!captchaSiteKey;
  const [turnstileToken, setTurnstileToken] = useState('');
  const [captchaNonce, setCaptchaNonce] = useState(0); // bump to remount/reset the widget
  function resetCaptcha() {
    setTurnstileToken('');
    setCaptchaNonce((n) => n + 1);
  }

  // Item 2 — inline forgot-password flow (email step)
  const [showForgotPortal, setShowForgotPortal] = useState(false);
  const [portalForgotEmail, setPortalForgotEmail] = useState('');
  const [portalForgotSubmitted, setPortalForgotSubmitted] = useState(false);
  // Item 4 — MFA lost-authenticator expand (portal)
  const { login } = usePortalAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oauthError = searchParams.get('error');

  const lookupMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/portal/auth/lookup-options', { email });
      return data.data as LookupOptionsResult;
    },
    onSuccess: (result) => {
      setLookupResult(result);
      setStep('credentials');
    },
    onError: () => {
      // On error, still advance to credentials step — show password + both IdP buttons (anti-enumeration)
      setLookupResult({ has_password: true, federated_providers: ['google', 'microsoft'] });
      setStep('credentials');
    },
  });

  const credentialsMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/portal/auth/login', {
        email,
        password,
        turnstileToken: showCaptcha ? turnstileToken : undefined,
      });
      if (!data.data) {
        const apiErr = data.errors?.[0] as { code?: string; message?: string } | undefined;
        const e = new Error(apiErr?.message ?? 'Login failed') as Error & { code?: string };
        if (apiErr?.code) e.code = apiErr.code;
        throw e;
      }
      return data.data as
        | { mfa_required: true; mfa_session_token: string; email_hint: string }
        | { sessionToken: string; vendor: { id: string; email: string } };
    },
    onSuccess: (data) => {
      if ('mfa_required' in data && data.mfa_required) {
        setMfaSessionToken(data.mfa_session_token);
        setEmailHint(data.email_hint);
        setStep('mfa');
      } else {
        const d = data as { sessionToken: string; vendor: { id: string } };
        login(d.sessionToken as PortalSessionToken, d.vendor.id);
        navigate('/portal/dashboard');
      }
    },
    onError: (err: any) => {
      // A Turnstile token is single-use; a failed attempt consumes it — reset for retry.
      if (showCaptcha) resetCaptcha();
      // 403 enrollment gate
      const enrollData = err?.response?.data?.data;
      if (enrollData?.mfaEnrollmentRequired) {
        navigate('/portal/mfa/enrollment', { state: { setupToken: enrollData.setupToken } });
        return;
      }
      if (err.code === 'reverification_required') {
        setStep('reverification');
        return;
      }
      // AC-SU.1-12: ACCOUNT_LOCKED (401) — transient, account may auto-unlock
      const errCode = err?.response?.data?.errors?.code ?? err?.response?.data?.errors?.[0]?.code;
      const status = err?.response?.status;
      if (status === 401 && errCode === 'ACCOUNT_LOCKED') {
        toast.error('Account temporarily locked due to too many failed attempts. Try again later.');
        return;
      }
      // AC-SU.1-12: ACCOUNT_DISABLED (403) — terminal, submit button disabled
      if (status === 403 && errCode === 'ACCOUNT_DISABLED') {
        setAccountDisabled(true);
        return;
      }
      toast.error('Invalid email or password');
    },
  });

  const mfaMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/portal/auth/mfa/challenge', {
        mfa_session_token: mfaSessionToken,
        totp_code: mfaToken.replace(/\s/g, ''),
      });
      if (!data.data) {
        const apiErr = data.errors?.[0] as { code?: string; message?: string } | undefined;
        const e = new Error(apiErr?.message ?? 'MFA verification failed') as Error & { code?: string };
        if (apiErr?.code) e.code = apiErr.code;
        throw e;
      }
      return data.data as { sessionToken: string; vendor: { id: string } };
    },
    onSuccess: (data) => {
      login(data.sessionToken as PortalSessionToken, data.vendor.id);
      navigate('/portal/home');
    },
    onError: (err: Error & { code?: string }) => {
      if (err.code === 'reverification_required') {
        setStep('reverification');
        return;
      }
      toast.error('Invalid or expired code — please try again');
    },
  });

  const portalForgotMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post('/portal/auth/forgot-password', { email: portalForgotEmail });
    },
    onSuccess: () => setPortalForgotSubmitted(true),
    onError: () => setPortalForgotSubmitted(true), // anti-enumeration: always show success
  });

  function handlePortalForgot(e: React.FormEvent) {
    e.preventDefault();
    if (!portalForgotEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(portalForgotEmail)) return;
    portalForgotMutation.mutate();
  }

  function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    lookupMutation.mutate();
  }

  function handleCredentials(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) return;
    credentialsMutation.mutate();
  }

  function handleMfa(e: React.FormEvent) {
    e.preventDefault();
    if (mfaToken.replace(/\s/g, '').length !== 6) {
      toast.error('Enter the 6-digit code from your authenticator app');
      return;
    }
    mfaMutation.mutate();
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-bg-base px-4">
      {/* Decorative accent glow — token-based, presentation only */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-1/2 h-[600px] w-[600px] -translate-x-1/2 rounded-full bg-accent-cyan/[0.08] blur-[120px]"
      />

      <div className="relative w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-4">
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-bg-surface-2 to-bg-base shadow-lg shadow-accent-cyan/20 ring-1 ring-accent-cyan/25">
            <img src="/verigrc-shield-256.png" alt="VeriGRC" className="h-8 w-8 object-contain" />
          </span>
          <div className="text-center">
            <h1 className="text-xl font-bold text-text-primary">Vendor Security Portal</h1>
            <p className="mt-1 text-sm text-text-secondary">Powered by VeriGRC</p>
          </div>
        </div>

        <div className="rounded-2xl border border-white/[0.07] bg-gradient-to-b from-bg-surface to-bg-base p-7 shadow-2xl">
          {oauthError && step === 'email' && (
          <p className="mb-4 rounded-md bg-accent-rose/10 px-3 py-2 text-sm text-accent-rose">
            {OAUTH_ERROR_MESSAGES[oauthError] ?? 'Sign-in could not be completed. Please try again.'}
          </p>
        )}

        {step === 'email' ? (
          <>
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-text-primary">Sign in</h2>
              <p className="mt-0.5 text-sm text-text-secondary">Access your vendor assessment portal</p>
            </div>
            <form onSubmit={handleEmailSubmit} className="space-y-4" noValidate>
              {!showForgotPortal && (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="pl-email">Email address</Label>
                    <Input
                      id="pl-email"
                      type="email"
                      placeholder="you@vendor.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      autoFocus
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="h-11 w-full rounded-lg bg-gradient-to-b from-accent-sky to-accent-cyan font-semibold shadow-lg shadow-accent-cyan/30 hover:opacity-95"
                    disabled={lookupMutation.isPending || !email}
                  >
                    {lookupMutation.isPending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Checking…</>
                    ) : (
                      'Continue'
                    )}
                  </Button>

                  {/* SSO (D-VENDOR-1-1) — both providers, always, on the email step.
                      OAuth init needs no email, so no lookup-options gating here. */}
                  <div className="flex items-center gap-3 py-1">
                    <span className="h-px flex-1 bg-border-subtle" />
                    <span className="text-xs font-medium text-text-faint">or continue with</span>
                    <span className="h-px flex-1 bg-border-subtle" />
                  </div>
                  <div className="space-y-2.5">
                    <OAuthLoginButton provider="google" />
                    <OAuthLoginButton provider="microsoft" />
                  </div>
                </>
              )}

              {/* Item 2 — inline forgot-password flow */}
              {!showForgotPortal && !portalForgotSubmitted && (
                <div className="mt-1 text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPortal(true)}
                    className="text-xs text-text-secondary hover:text-text-primary transition-colors"
                  >
                    Trouble signing in?
                  </button>
                </div>
              )}
              {showForgotPortal && !portalForgotSubmitted && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-text-secondary text-center">
                    Enter the email address associated with your vendor portal invitation. If we find an eligible account, we'll send next steps.
                  </p>
                  <form onSubmit={handlePortalForgot} className="space-y-2" noValidate>
                    <Input
                      type="email"
                      placeholder="you@vendor.com"
                      aria-label="Forgot password email"
                      value={portalForgotEmail}
                      onChange={(e) => setPortalForgotEmail(e.target.value)}
                      autoComplete="email"
                      required
                    />
                    <Button
                      type="submit"
                      className="w-full"
                      size="sm"
                      disabled={portalForgotMutation.isPending || !portalForgotEmail}
                    >
                      {portalForgotMutation.isPending
                        ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Sending…</>
                        : 'Send reset link'}
                    </Button>
                    <button
                      type="button"
                      onClick={() => setShowForgotPortal(false)}
                      className="w-full text-center text-xs text-text-secondary hover:text-text-primary transition-colors"
                    >
                      ← Back to sign in
                    </button>
                  </form>
                </div>
              )}
              {portalForgotSubmitted && (
                <p className="mt-3 text-center text-xs text-text-secondary">
                  Check your email — if an eligible account was found, reset instructions have been sent.
                </p>
              )}
            </form>
          </>
        ) : step === 'reverification' ? (
            <div className="flex flex-col items-center gap-4 py-2 text-center">
              <div>
                <h2 className="text-lg font-semibold text-text-primary">Check your email to reactivate your account</h2>
                <p className="mt-1.5 text-sm text-text-secondary">
                  We've sent a reactivation link to{' '}
                  <span className="font-medium text-text-primary">{email}</span>.
                  Click the link in the email to restore access.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStep('credentials')}
                className="text-xs text-text-secondary hover:text-text-primary transition-colors"
              >
                ← Back to sign in
              </button>
            </div>
          ) : step === 'credentials' ? (
            <>
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-text-primary">Welcome back</h2>
                <div className="mt-3 flex items-center gap-2.5 rounded-lg border border-white/[0.07] bg-white/[0.03] p-1.5 pl-2">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-gradient-to-br from-accent-cyan-dim to-bg-surface-2 text-xs font-bold uppercase text-accent-sky ring-1 ring-accent-cyan/25">
                    {email.charAt(0) || '?'}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-text-secondary">{email}</span>
                  <button
                    type="button"
                    className="shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-accent-cyan hover:bg-accent-cyan/10"
                    onClick={() => { setStep('email'); setLookupResult(null); setPassword(''); }}
                  >
                    Change
                  </button>
                </div>
              </div>

              {/* Password sign-in — shown when account has password OR no account (anti-enumeration) */}
              {(lookupResult?.has_password || lookupResult?.has_password === false) && (
                <form onSubmit={handleCredentials} className="space-y-4" noValidate>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="pl-password">Password</Label>
                      <Link
                        to="/portal/forgot-password"
                        className="text-xs text-accent-cyan hover:underline"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <div className="relative">
                      <Input
                        id="pl-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        required
                        autoFocus
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((p) => !p)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary focus-visible:outline-none"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {accountDisabled && (
                    <p className="text-sm text-accent-rose rounded-md bg-accent-rose/10 px-3 py-2">
                      This account has been disabled. Contact your administrator for assistance.
                    </p>
                  )}

                  {showCaptcha && (
                    <TurnstileWidget
                      key={captchaNonce}
                      siteKey={captchaSiteKey!}
                      onToken={setTurnstileToken}
                      onExpire={() => setTurnstileToken('')}
                    />
                  )}

                  <Button
                    type="submit"
                    className="h-11 w-full rounded-lg bg-gradient-to-b from-accent-sky to-accent-cyan font-semibold shadow-lg shadow-accent-cyan/30 hover:opacity-95"
                    disabled={credentialsMutation.isPending || !password || accountDisabled || (showCaptcha && !turnstileToken)}
                  >
                    {credentialsMutation.isPending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Signing in…</>
                    ) : (
                      'Sign in'
                    )}
                  </Button>
                </form>
              )}

              {/* SSO/OAuth buttons moved to the email step (step 1) — D-VENDOR-1-1. Not duplicated here.
                  The SSO help text (AC-SSL-3) intentionally stays on the credentials step. */}
              <div className="mt-5 flex gap-2.5 border-t border-white/[0.06] pt-4">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-text-faint" />
                <p className="text-xs leading-relaxed text-text-secondary">
                  Using SSO? Please contact your organization's SSO administrator for access or identity provider issues.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="mb-3 flex items-center gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-accent-cyan/10 text-accent-cyan ring-1 ring-accent-cyan/25">
                  <ShieldCheck className="h-5 w-5" />
                </span>
                <h2 className="text-lg font-semibold text-text-primary">Two-factor authentication</h2>
              </div>
              {emailHint && (
                <p className="mb-5 text-sm text-text-secondary">
                  Signing in as <span className="font-medium text-text-primary">{emailHint}</span>
                </p>
              )}

              <form onSubmit={handleMfa} className="space-y-4" noValidate>
                <div className="space-y-1.5">
                  <Label htmlFor="pl-mfa">Authenticator code</Label>
                  <Input
                    id="pl-mfa"
                    type="text"
                    inputMode="numeric"
                    placeholder="000 000"
                    value={mfaToken}
                    onChange={(e) => setMfaToken(e.target.value.replace(/[^\d\s]/g, ''))}
                    maxLength={7}
                    autoFocus
                    autoComplete="one-time-code"
                    className="h-12 text-center text-xl font-mono tracking-[0.4em]"
                  />
                  <p className="text-xs text-text-secondary">
                    Enter the 6-digit code from your authenticator app.
                  </p>
                </div>

                <Button
                  type="submit"
                  className="h-11 w-full rounded-lg bg-gradient-to-b from-accent-sky to-accent-cyan font-semibold shadow-lg shadow-accent-cyan/30 hover:opacity-95"
                  disabled={mfaMutation.isPending || mfaToken.replace(/\s/g, '').length < 6}
                >
                  {mfaMutation.isPending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verifying…</>
                  ) : (
                    'Verify'
                  )}
                </Button>

                <div className="flex items-center justify-between pt-1 text-xs">
                  <button
                    type="button"
                    onClick={() => setStep('credentials')}
                    className="text-text-secondary hover:text-text-primary"
                  >
                    ← Back
                  </button>
                </div>
                {/* VMFA-2 — self-service MFA recovery (replaces the static "contact the organization" copy).
                    Launches the real flow: emails a recovery link via PortalMfaRecoverPage. */}
                <div className="flex gap-2.5 border-t border-white/[0.06] pt-4">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-text-faint" />
                  <p className="text-xs leading-relaxed text-text-secondary">
                    <button
                      type="button"
                      onClick={() => navigate(`/portal/mfa/recover?mfa_session_token=${encodeURIComponent(mfaSessionToken)}`)}
                      disabled={!mfaSessionToken}
                      className="font-medium text-accent-cyan transition-colors hover:underline disabled:opacity-50"
                    >
                      Lost access to your authenticator?
                    </button>
                    {' '}We'll email you a recovery link to reset your authenticator.
                  </p>
                </div>
              </form>
            </>
          )}
        </div>

        {/* Trust footer — accurate signals only (no SOC 2 / certification claim).
            "Privacy" is plain text, not a dead link, per the no-facade principle. */}
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-text-faint">
          <Lock className="h-3.5 w-3.5" />
          <span>Encrypted connection</span>
          <span className="h-1 w-1 rounded-full bg-text-faint/60" />
          <span>Privacy</span>
        </div>
      </div>
    </div>
  );
}
